// Here, this might be an improvement:
// https://xrutayisire.github.io/react-js-cron/?path=/docs/reactjs-cron--demo

import { CronExpressionParser } from "cron-parser";
import moment from "moment-timezone";
import React from "react";
import { Button, Card, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import {
    FaCode,
    FaCopy,
    FaDatabase,
    FaEdit,
    FaHistory,
    FaStopwatch,
    FaTrash,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router";

import { getFirebaseConsoleUrl } from "../../api/firestoreBaseApi.jsx";
import {
    RoutineCollection,
    RoutineDocument,
    useLocationDocument,
    useMissionDocument,
    useRoutineDocument,
    useTaskDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { routineChangelogRoute } from "../../changelogRoutes.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import DevMenuButton from "../../components/DevMenuButton";
import Layout, { Title } from "../../components/Layout/Layout";
import RoutinePreview from "../../components/RoutinePreview";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";

export default function RoutinePage() {
    const { organizationId, locationId, routineId } = useParams();
    const [routine] = useRoutineDocument(organizationId, locationId, routineId);
    const [showFirebaseData, setShowFirebaseData] = React.useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const navigate = useNavigate();
    const [routineTask] = useTaskDocument(
        organizationId,
        locationId,
        routine?.taskId,
    );
    const [routineMission] = useMissionDocument(
        organizationId,
        locationId,
        routine?.missionId,
    );
    const [location] = useLocationDocument(organizationId, locationId);
    const { getUserIsAdmin } = useUser();

    async function onDuplicateRoutine() {
        const routineCopy = { ...routine, name: `${routine.name} (copy)` };
        delete routineCopy.id;
        const newRoutineId = await RoutineCollection(
            organizationId,
            locationId,
        ).add(routineCopy);

        navigate(urls.routine(organizationId, locationId, newRoutineId));
    }

    function onDeleteRoutine() {
        RoutineDocument(organizationId, locationId, routineId)
            .delete()
            .then(() => navigate(urls.routines(organizationId, locationId)));
    }

    function handleClose() {
        setShowDeleteModal(false);
    }

    function handleShow() {
        setShowDeleteModal(true);
    }

    function visitFirestore() {
        window.open(
            getFirebaseConsoleUrl(
                RoutineDocument(organizationId, locationId, routineId).path,
            ),
        );
    }

    let routineDisplayContent;
    try {
        routineDisplayContent = routine ? (
            <>
                <RoutinePreview
                    className="m-0 mb-4"
                    location={location}
                    routineTask={routineTask}
                    routineMission={routineMission}
                    routine={routine}
                />
                <Form.Check
                    type="switch"
                    id={`routine-toggle-${routine.id}`}
                    label={
                        routine.enabled
                            ? "Routine is enabled"
                            : "Routine is disabled"
                    }
                    checked={routine.enabled}
                    onChange={() =>
                        RoutineDocument(
                            organizationId,
                            locationId,
                            routine.id,
                        ).update({ enabled: !routine.enabled })
                    }
                />
                {routine.enabled && (
                    <div className="mt-3">
                        Next mission will start at{" "}
                        {
                            <code>
                                <TimeDisplay
                                    timestamp={moment(
                                        CronExpressionParser.parse(
                                            routine.interval,
                                            { tz: location?.timezone || "UTC" },
                                        )
                                            .next()
                                            .toDate(),
                                    ).unix()}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    organizationId={organizationId}
                                    locationId={locationId}
                                />
                            </code>
                        }
                    </div>
                )}
            </>
        ) : (
            <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        );
    } catch (error) {
        console.log("Unable to parse routine", error);
        routineDisplayContent = "Routine is invalid";
    }

    return (
        <Layout>
            <title>{`Routine ${routine?.name || ""} | MyCorvus`}</title>
            <Modal show={showDeleteModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Routine</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to permanently delete routine{" "}
                    <b>{routine?.name}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={onDeleteRoutine}>
                        <FaTrash
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            <Breadcrumbs
                path={breadcrumbPaths.routine}
                location={location}
                routine={routine ?? null}
            />
            {
                <Card className="m-0 mb-4">
                    <Title
                        title={routine?.name || "Routine"}
                        backButtonUrl={urls.routines(
                            organizationId,
                            locationId,
                        )}
                    >
                        {getUserIsAdmin() && (
                            <DevMenuButton className="ms-1 mb-1">
                                <Dropdown.Item
                                    as={Link}
                                    to={`${urls.demo()}?organizationId=${organizationId}&locationId=${locationId}&routineId=${routineId}`}
                                >
                                    <FaStopwatch
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    Demo page
                                </Dropdown.Item>
                                <Dropdown.Item onClick={visitFirestore}>
                                    <FaDatabase
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    View Firestore
                                </Dropdown.Item>
                                {showFirebaseData ? (
                                    <Dropdown.Item
                                        onClick={() =>
                                            setShowFirebaseData(false)
                                        }
                                    >
                                        <FaCode
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Hide data
                                    </Dropdown.Item>
                                ) : (
                                    <Dropdown.Item
                                        onClick={() =>
                                            setShowFirebaseData(true)
                                        }
                                    >
                                        <FaCode
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Show Data
                                    </Dropdown.Item>
                                )}
                                <Dropdown.Item
                                    onClick={() =>
                                        navigate(
                                            routineChangelogRoute({
                                                organizationId,
                                                locationId,
                                                routineId,
                                            }),
                                        )
                                    }
                                >
                                    <FaHistory
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    Changelogs
                                </Dropdown.Item>
                            </DevMenuButton>
                        )}
                        <Button
                            variant="outline-secondary"
                            as={Link}
                            to={urls.routineSettings(
                                organizationId,
                                locationId,
                                routineId,
                            )}
                            className="ms-1 mb-1"
                            aria-label="Edit"
                        >
                            <FaEdit style={{ marginTop: "-2px" }} />
                            <span className="d-none d-md-inline ms-2">
                                Edit
                            </span>
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={onDuplicateRoutine}
                            className="ms-1 mb-1"
                            aria-label="Duplicate"
                        >
                            <FaCopy style={{ marginTop: "-2px" }} />
                            <span className="d-none d-md-inline ms-2">
                                Duplicate
                            </span>
                        </Button>
                        <Button
                            variant="outline-danger"
                            onClick={handleShow}
                            className="ms-1 mb-1"
                            aria-label="Delete"
                        >
                            <FaTrash style={{ marginTop: "-2px" }} />
                            <span className="d-none d-md-inline ms-2">
                                Delete
                            </span>
                        </Button>
                    </Title>
                    <Card.Body className="p-4 pt-0">
                        {routineDisplayContent}
                    </Card.Body>
                    {showFirebaseData && routine && (
                        <Card.Body className="p-4 pt-0">
                            <CodeBlock name="Routine" code={routine} />
                        </Card.Body>
                    )}
                </Card>
            }
        </Layout>
    );
}
